import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { AlertTriangle, Building2, FlaskConical, RefreshCw, Search, UserPlus } from "lucide-react";
import { CockpitQuickView } from "@/components/common/CockpitQuickView";
import { useAuth } from "@/context/AuthContext";
import { useT52 } from "@/i18n/v52";
import { Empty52, useCanSee } from "@/components/v52/Bits";
import { OrgSection } from "@/components/v52/KundenParts";

function SearchBar({ q, setQ, inputRef }) {
  const { t52 } = useT52();
  const can = useCanSee();
  return (
    <section className="f52-panel" aria-label={t52("k_searchL")}>
      <form className="f52-toolbar" role="search" onSubmit={(e) => e.preventDefault()}>
        <div className="f52-search">
          <label htmlFor="org-search">{t52("k_searchL")}</label>
          <div className="wrap">
            <Search aria-hidden="true" />
            <input id="org-search" ref={inputRef} type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t52("k_searchPh")}
              aria-describedby="org-search-hint" autoComplete="off" data-testid="org-search" />
          </div>
          <p className="hint" id="org-search-hint">{t52("k_searchHint")}</p>
        </div>
        {can("onboarding") && <Link className="f52-btn f52-btn-secondary" to="/onboarding" data-testid="org-onboarding-link"><UserPlus aria-hidden="true" />{t52("k_onb")}</Link>}
      </form>
    </section>
  );
}

export default function BrockiList() {
  const { user, env } = useAuth();
  const { t } = useTranslation();
  const { t52 } = useT52();
  const isSuper = user?.role === "super_admin";
  const isPreview = !env?.is_production;
  const [state, setState] = useState(null);
  const [q, setQ] = useState("");
  const [qvTenant, setQvTenant] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    setState(null);
    const scope = isSuper ? "all" : "customers";
    api.get("/organizations", { params: { scope } }).then((r) => setState(r.data)).catch(() =>
      setState({ service_available: false, reason: null, organizations: [] })
    );
  }, [isSuper]);
  useEffect(() => { load(); }, [load]);

  const term = q.trim().toLowerCase();
  const all = state?.organizations || [];
  const match = (o) => !term || `${o.company_name || ""} ${o.tenant_id || ""} ${o.city || ""}`.toLowerCase().includes(term);
  const custAll = all.filter((o) => !o.is_demo && o.customer_status === "active_customer");
  const qaAll = isPreview && isSuper ? all.filter((o) => o.is_demo) : [];
  const cockpitTenants = all.map((o) => ({ tenant_id: o.tenant_id, company_name: o.company_name, plan: o.plan, account_mode: o.account_mode }));
  const reset = () => { setQ(""); inputRef.current?.focus(); };
  const common = { q: q.trim(), onReset: reset, isSuper, onCockpit: setQvTenant };

  return (
    <div className="f52-page" data-testid="fundio-kunden-page">
      <section className="f52-page-head">
        <h1>{t("nav.items.kunden")}</h1>
        <p>{t52("k_lead")}</p>
      </section>
      <SearchBar q={q} setQ={setQ} inputRef={inputRef} />

      {state === null ? (
        <section className="f52-panel" data-testid="org-loading" aria-busy="true">
          <span className="f52-sr">{t52("loading")}</span><span className="f52-skel block" />
        </section>
      ) : !state.service_available ? (
        <section className="f52-panel">
          <Empty52 icon={AlertTriangle} tone="warn" role="alert" title={t52("k_downT")} text={state.reason || t52("k_downD")} testid="org-unavailable">
            <button type="button" className="f52-btn f52-btn-primary" onClick={load} data-testid="org-retry-button"><RefreshCw aria-hidden="true" />{t52("retry")}</button>
          </Empty52>
        </section>
      ) : (
        <>
          <OrgSection {...common} testid="org-panel" title={t52("k_customers")} rows={custAll.filter(match)} total={custAll.length}
            chip={<span className="f52-chip-src">{t52(isSuper ? "k_scopeAll" : "k_scopeCust")}</span>}
            empty={<Empty52 icon={Building2} title={t52("k_emptyT")} text={t52("k_emptyD")} testid="org-list-empty" />} />
          {qaAll.length > 0 && (
            <>
              <div className="f52-qa-note" role="note" data-testid="qa-preview-note"><strong>PREVIEW / INTERNAL QA</strong><span>{t52("k_qaNote")}</span></div>
              <OrgSection {...common} testid="org-qa-panel" title={t52("k_qa")} rows={qaAll.filter(match)} total={qaAll.length}
                chip={<span className="f52-chip-src"><FlaskConical aria-hidden="true" />{t52("k_qaChip")}</span>} />
            </>
          )}
        </>
      )}

      {qvTenant && <CockpitQuickView tenants={cockpitTenants} initialTenantId={qvTenant} onClose={() => setQvTenant(null)} />}
    </div>
  );
}
